import React, { useMemo } from "react";
import DatePartsInput from "./DatePartsInput";

function shouldUseDatePartsInput() {
  if (typeof navigator === "undefined") {
    return false;
  }

  const userAgent = navigator.userAgent || "";
  const platform = navigator.platform || "";
  const maxTouchPoints = Number(navigator.maxTouchPoints) || 0;
  const isIosDevice =
    /iPad|iPhone|iPod/.test(userAgent) ||
    (platform === "MacIntel" && maxTouchPoints > 1);

  return isIosDevice;
}

/**
 * DatePickerInput
 * ---------------
 * A calendar/date picker that always returns an ISO-like string "yyyy-mm-dd".
 * - Native <input type="date"> by default (no deps).
 * - Enforces min="1900-01-01" and max=<today>.
 *
 * Props:
 * • value      – string "yyyy-mm-dd" or ""
 * • onChange   – (syntheticEventLike) => void   (matches normal <input/> handlers)
 * • id         – string (field id / name)
 * • required   – boolean
 * • className  – optional wrapper classes
 * • preferDateParts – render split DD/MM/YYYY inputs regardless of platform
 */
export default function DatePickerInput({
  value = "",
  onChange,
  id,
  required,
  className = "",
  preferDateParts = false,
}) {
  const useDatePartsInput = useMemo(
    () => preferDateParts || shouldUseDatePartsInput(),
    [preferDateParts],
  );
  const todayStr = useMemo(() => {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  }, []);

  const MIN = "1900-01-01";

  const handleChange = (e) => {
    // Native <input type="date"> gives "yyyy-mm-dd" or "".
    const next = e.target.value;

    // Re-emit in the same event shape your form expects:
    onChange({
      target: { name: id, value: next },
    });
  };

  if (useDatePartsInput) {
    return (
        <DatePartsInput
          id={id}
          value={value}
          onChange={onChange}
          required={required}
          className={["w-full min-w-0 max-w-full", className]
            .filter(Boolean)
            .join(" ")}
        />
    );
  }

  return (
    <div className={["w-full min-w-0", className].filter(Boolean).join(" ")}>
      <input
        id={id}
        name={id}
        type="date"
        className="custom-input block w-full min-w-0 max-w-full"
        value={value || ""}
        onChange={handleChange}
        min={MIN}
        max={todayStr} // no future dates
        required={required}
      />
    </div>
  );
}
